/**
 * SLA countdown formatting and colour (IMPLEMENTATION_PLAN §9.2). The API sends
 * computed SLA state; this module only formats `dueAt − now` and picks a tone.
 * It holds no SLA business rules.
 */

export type DateInput = string | number | Date | null | undefined

/** SLA state as the API returns it on a ticket. */
export interface SlaState {
  dueAt: DateInput
  warnAt?: DateInput
  breached?: boolean
  clockRunning?: boolean
  /** Paused clocks: business minutes left when the clock stopped (the API's engine). Null while running. */
  remainingMins?: number | null
}

export type SlaTone = 'breach' | 'critical' | 'warning' | 'normal' | 'none'

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

function toMs(value: DateInput): number | null {
  if (value === null || value === undefined || value === '') return null
  const ms = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Remaining time as shown in the queue's SLA column:
 * - under 24h → `h:mm` (`0:42`, `3:50`, `23:59`)
 * - 24h or more → whole days (`2d`)
 * - no due date → `—`
 * - overdue → the same formats with a leading minus (`−0:15`, `−3d`); the
 *   overdue magnitude rounds up so a just-breached ticket never reads `−0:00`.
 */
export function formatRemaining(dueAt: DateInput, now: DateInput = Date.now()): string {
  const due = toMs(dueAt)
  const at = toMs(now)
  if (due === null || at === null) return '—'

  const diff = due - at
  const overdue = diff < 0
  const abs = Math.abs(diff)

  if (abs >= DAY) {
    const days = Math.floor(abs / DAY)
    return `${overdue ? '−' : ''}${days}d`
  }
  const totalMinutes = overdue ? Math.ceil(abs / MINUTE) : Math.floor(abs / MINUTE)
  if (overdue && totalMinutes >= 24 * 60) return '−1d'
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${overdue ? '−' : ''}${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * Tone for the countdown, most urgent first:
 * - `breach`   — the API marked it breached, or the due time has passed
 * - `critical` — less than 1 hour left
 * - `warning`  — past the policy's warn point (`warnAt`)
 * - `normal`   — otherwise
 * - `none`     — no due date and not breached (renders `—`)
 */
export function slaTone(sla: SlaState | null | undefined, now: DateInput = Date.now()): SlaTone {
  if (!sla) return 'none'
  if (sla.breached) return 'breach'
  const due = toMs(sla.dueAt)
  const at = toMs(now)
  if (due === null || at === null) return 'none'
  const remaining = due - at
  if (remaining <= 0) return 'breach'
  if (remaining < HOUR) return 'critical'
  const warn = toMs(sla.warnAt)
  if (warn !== null && at >= warn) return 'warning'
  return 'normal'
}

/** Tailwind classes per tone (colours from artboard 01's SLA column). */
export const SLA_TONE_CLASS: Record<SlaTone, string> = {
  breach: 'text-sla-breach font-semibold',
  critical: 'text-sla-critical font-medium',
  warning: 'text-sla-warning font-medium',
  normal: 'text-sla-normal',
  none: 'text-sla-none',
}

/**
 * A paused clock's remaining BUSINESS minutes as `h:mm` (`2:10`, `24:00`).
 * Never converted to days: 24 business hours are three working days, not one.
 */
export function formatMinutesLeft(mins: number): string {
  const m = Math.max(0, Math.floor(mins))
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`
}

export interface SlaCell {
  /** The countdown (`0:42`, `−3d`, `—`); for a paused clock the time left at pause, or '' when unknown. */
  text: string
  tone: SlaTone
  /**
   * Clock paused (waiting on customer, scheduled): the countdown is frozen.
   * Render it as "Paused · {text} left" (or "Paused" when `text` is ''),
   * never as overdue — a paused ticket's projected due date can lie in the past.
   */
  paused: boolean
}

const DONE_STATUSES = new Set(['resolved', 'closed'])

/**
 * What the queue's SLA column (and the rail) shows for a ticket. Resolved and
 * closed tickets show `—` (artboard 01, TKT-04785). The clock state is checked
 * first: a paused clock shows the API's `remainingMins` in the neutral tone,
 * whatever the due date or breach flag say.
 */
export function slaCell(
  sla: SlaState | null | undefined,
  status: string | null | undefined,
  now: DateInput = Date.now(),
): SlaCell {
  if (!sla || DONE_STATUSES.has((status ?? '').toLowerCase())) return { text: '—', tone: 'none', paused: false }
  if (sla.clockRunning === false) {
    const left = sla.remainingMins
    return { text: typeof left === 'number' ? formatMinutesLeft(left) : '', tone: 'normal', paused: true }
  }
  return { text: formatRemaining(sla.dueAt, now), tone: slaTone(sla, now), paused: false }
}
